// App.tsx
import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'

import { Navigation } from './components/layout/Navigation'
import { Hero } from './components/sections/Hero'
import { Intro } from './components/sections/Intro'
import { FeaturedWork } from './components/sections/FeaturedWork'
import { Contact } from './components/sections/Contact'
import { Footer } from './components/layout/Footer'
import { ReadingProgress } from './components/ui/ReadingProgress'
import { BackToTop } from './components/ui/BackToTop'
import { CookieConsent } from './components/ui/CookieConsent'
import { ResourcePreload } from './components/ui/ResourcePreload'
import { Toaster } from 'sonner'
import { lazyLoadComponent, prefetchComponent } from './utils/lazyLoad'
import { SecurityProvider } from './theme/SecurityContext'
import { SEO } from './components/SEO/SEO'
import { bio } from '@data/ResumeFiles/bio'

/* ------------ lazy‑loaded pages ------------ */
const Blog           = lazyLoadComponent(() => import('./pages/blog/Blog'))
const BlogPost       = lazyLoadComponent(() => import('./components/blog/BlogPost'))
const Research       = lazyLoadComponent(() => import('./pages/research/ResearchPage'))
const ResearchDetail = lazyLoadComponent(() => import('./pages/research/ResearchDetail'))
const About          = lazyLoadComponent(() => import('./pages/resume/About'))
const PrivacyPolicy  = lazyLoadComponent(() => import('./pages/legal/PrivacyPolicy'))
const Terms          = lazyLoadComponent(() => import('./pages/legal/Terms'))
const CookiePolicy   = lazyLoadComponent(() => import('./pages/legal/CookiePolicy'))
const Sitemap        = lazyLoadComponent(() => import('./pages/Sitemap'))

const MainContent = () => (
  <>
    <SEO
      title={`${bio.name} – Homepage`}
      description={`${bio.name}'s personal website.`}
      image="https://www.zeinalamah.com/favicon.svg"
      url="https://www.zeinalamah.com"
      type="website"
    />

    <Hero />
    <Intro />
    <FeaturedWork />
    <Contact />
  </>
)

const App: React.FC = () => {
  /* optional prefetch */
  useEffect(() => {
    const t = setTimeout(() => {
      prefetchComponent(() => import('./pages/blog/Blog'))
      prefetchComponent(() => import('./pages/research/ResearchPage'))
    }, 2000)
    return () => clearTimeout(t)
  }, [])

  return (
    <HelmetProvider>
      <SecurityProvider>
        <div className="min-h-screen bg-background">
          <ResourcePreload />
          <ReadingProgress />
          <Navigation />

          <main id="main-content">
            <Routes>
              <Route path="/" element={<MainContent />} />
              <Route path="/about" element={<About />} />

              {/* LIST + DETAIL */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              <Route path="/research" element={<Research />} />
              <Route path="/research/:slug" element={<ResearchDetail />} />

              {/* legal */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/sitemap" element={<Sitemap />} />
            </Routes>
          </main>

          <Footer />
          <BackToTop />
          <CookieConsent />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#2B3187',
                color: '#F7F8F8',
                border: '1px solid rgba(43, 49, 135, 0.2)',
              },
            }}
          />
        </div>
      </SecurityProvider>
    </HelmetProvider>
  )
}

export default App
